import React, { useMemo, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableRow, Button, CircularProgress, Alert } from '@mui/material';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import  DashboardUsersTable from './DashboardUsersTable';
import TTFTByModelChart from './PerformanceMetrics/TTFTByModelChart';
import DailyMessageChart from './PerformanceMetrics/DailyMessageChart';
import DurationByAgentChart from './PerformanceMetrics/DurationByAgentChart';
import { UserMetrics } from '@/api/dashboard';
import { useModelUsageQuery, useUsersWithMetricsQuery, useBusinessUnitCostsQuery, useMostActiveUsers2Query, useMonthlyTotalCostsQuery, useTTFTByModelQuery, useDurationByAgentQuery, useDailyMessageActivityQuery, useMessageTotalsQuery } from '../../features/dashboard/hooks/useDashboardQuery';
import { queryKeys } from '@/lib/queryKeys';
import { QueryErrorState, QueryStaleDataWarning } from '@/components/state';

const EMPTY_USERS: UserMetrics[] = [];

const DashboardTab: React.FC = () => {
  const queryClient = useQueryClient();
  const toStartOfDayIso = (date: Date): string => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.toISOString();
  };

  const toEndOfDayIso = (date: Date): string => {
    const normalized = new Date(date);
    normalized.setHours(23, 59, 59, 999);
    return normalized.toISOString();
  };

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  
  // Date range state - Default: last 30 days
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    date.setHours(0, 0, 0, 0);
    return date;
  };
  
  const [startDate, setStartDate] = useState<Date | null>(getDefaultStartDate());
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  };

  const [endDate, setEndDate] = useState<Date | null>(getDefaultEndDate());
  const [processedUsers, setProcessedUsers] = useState<UserMetrics[]>([]);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const startDateParam = useMemo(
    () => (startDate ? toStartOfDayIso(startDate) : ''),
    [startDate]
  );
  const endDateParam = useMemo(
    () => (endDate ? toEndOfDayIso(endDate) : ''),
    [endDate]
  );

  //const {data:mostActiveUsers, isLoading:loadingUsers, error: errorUsers} = useMostActiveUsersQuery(startDate?.toISOString() || '', endDate?.toISOString() || '', 5, !!(startDate && endDate));
  const {
    data: modelUsage,
    isLoading: loadingModels,
    error: errorModels,
    isRefetchError: isModelUsageRefetchError,
  } = useModelUsageQuery(startDateParam, endDateParam, 5, !!(startDate && endDate));
  const {
    data: messageTotals,
    isLoading: loadingTotals,
    error: errorTotals,
    isRefetchError: isMessageTotalsRefetchError,
  } = useMessageTotalsQuery(startDateParam, endDateParam, !!(startDate && endDate));
  const {
    data: usersWithMetrics,
    isLoading: loadingUsersWithMetrics,
    error: errorUsersWithMetrics,
    isRefetchError: isUsersWithMetricsRefetchError,
  } = useUsersWithMetricsQuery(startDateParam, endDateParam, !!(startDate && endDate));
  const usersWithMetricsForTable = useMemo(
    () => usersWithMetrics ?? EMPTY_USERS,
    [usersWithMetrics]
  );
  const {
    data: businessUnitCosts,
    isLoading: loadingBusinessUnitCosts,
    error: errorBusinessUnitCosts,
    isRefetchError: isBusinessUnitRefetchError,
  } = useBusinessUnitCostsQuery(startDateParam, endDateParam, !!(startDate && endDate));
  const {
    data: mostActiveUsers,
    isLoading: loadingUsers,
    error: errorUsers,
    isRefetchError: isMostActiveUsersRefetchError,
  } = useMostActiveUsers2Query(startDateParam, endDateParam, 5, !!(startDate && endDate));
  const {
    data: monthlyTotalCosts,
    isLoading: loadingMonthlyTotalCosts,
    error: errorMonthlyTotalCosts,
    isRefetchError: isMonthlyCostsRefetchError,
  } = useMonthlyTotalCostsQuery('userId', 'userRole', true, 12);

  const {
    data: ttftByModelResponse,
    isLoading: loadingTTFTByModel,
    error: errorTTFTByModel,
  } = useTTFTByModelQuery(startDateParam, endDateParam, !!(startDate && endDate));

  const {
    data: durationByAgentResponse,
    isLoading: loadingDurationByAgent,
    error: errorDurationByAgent,
  } = useDurationByAgentQuery(startDateParam, endDateParam, !!(startDate && endDate));

  const {
    data: dailyMessageActivityResponse,
    isLoading: loadingDailyMessageActivity,
    error: errorDailyMessageActivity,
    isRefetchError: isDailyMessageActivityRefetchError,
  } = useDailyMessageActivityQuery(startDateParam, endDateParam, !!(startDate && endDate));

  const hasAnyDashboardData = Boolean(
    (usersWithMetrics && usersWithMetrics.length > 0) ||
    (mostActiveUsers && mostActiveUsers.length > 0) ||
    (modelUsage && modelUsage.length > 0) ||
    (businessUnitCosts && businessUnitCosts.length > 0) ||
    (messageTotals && messageTotals.total_messages > 0) ||
    (monthlyTotalCosts && monthlyTotalCosts.length > 0) ||
    (dailyMessageActivityResponse?.metrics && dailyMessageActivityResponse.metrics.length > 0)
  );
  const dashboardErrors = [
    errorUsersWithMetrics,
    errorUsers,
    errorModels,
    errorBusinessUnitCosts,
    errorTotals,
    errorMonthlyTotalCosts,
    errorDailyMessageActivity,
  ].filter(Boolean) as Error[];
  const hasAnyDashboardError = dashboardErrors.length > 0;
  const showDashboardStaleWarning = hasAnyDashboardData && (
    isUsersWithMetricsRefetchError ||
    isMostActiveUsersRefetchError ||
    isModelUsageRefetchError ||
    isBusinessUnitRefetchError ||
    isMessageTotalsRefetchError ||
    isMonthlyCostsRefetchError ||
    isDailyMessageActivityRefetchError
  );
  const showDashboardBlockingError = !hasAnyDashboardData && hasAnyDashboardError;
  const dashboardErrorMessage = hasAnyDashboardError
    ? dashboardErrors[0].message
    : 'Failed to load dashboard data';

  const retryDashboardQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  };

  // ==========================================
  // EXPORT & PRINT HANDLERS
  // ==========================================

  // Export CSV handler
  const handleExportCSV = () => {
    if (processedUsers.length === 0) {
      alert('No users to export');
      return;
    }

    // CSV headers
    const headers = [
      'User Name',
      'Status',
      'Business Unit',
      'Total Messages',
      'Total Cost (€)',
      'Input Tokens',
      'Output Tokens',
      'Most Used Model'
    ];

    // CSV rows
    const rows = processedUsers.map(user => [
      user.display_name || '',
      user.status || '',
      user.business_unit || 'Unassigned',
      user.total_messages || 0,
      user.total_cost?.toFixed(2) || '0.00',
      user.tokens_input || 0,
      user.tokens_output || 0,
      user.most_used_model || ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row
          .map(cell => {
            // Escape quotes and wrap in quotes if contains comma or quote
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(',')
      )
    ].join('\n');

    // Create blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `bolt-users-export-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print handler
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    // Clone the content to manipulate without affecting the original
    const clonedContent = printContent.cloneNode(true) as HTMLElement;

    // Fix chart titles - make them larger and dark, reduce margin
    clonedContent.querySelectorAll('h6, .MuiTypography-h6, .MuiTypography-root').forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.textContent && !htmlEl.closest('table')) {
        htmlEl.style.fontSize = '16px';
        htmlEl.style.fontWeight = 'bold';
        htmlEl.style.color = '#000';
        htmlEl.style.marginBottom = '8px';
      }
    });

    // Fix all SVG text elements (legend, axis labels, etc.)
    clonedContent.querySelectorAll('text, tspan').forEach((el) => {
      el.setAttribute('fill', '#000');
      (el as HTMLElement).style.fill = '#000';
    });

    // Fix legend text - target spans inside recharts legend wrapper
    clonedContent.querySelectorAll('.recharts-legend-wrapper span, .recharts-legend-item-text').forEach((el) => {
      (el as HTMLElement).style.color = '#000';
    });

    // Fix table text
    clonedContent.querySelectorAll('td, th, tr').forEach((el) => {
      (el as HTMLElement).style.color = '#000';
    });

    // Adjust card heights for print (reduce from 300px to auto for better layout)
    clonedContent.querySelectorAll('.MuiCard-root').forEach((el) => {
      (el as HTMLElement).style.height = 'auto';
      (el as HTMLElement).style.minHeight = 'auto';
    });

    // Remove only the large paginated users table (not the small summary tables in cards)
    // Find and remove tables that are NOT inside the dashboard cards
    const dashboardPrintElement = clonedContent.querySelector('.dashboard-print');
    clonedContent.querySelectorAll('table').forEach((el) => {
      // Skip tables that are inside dashboard cards
      if (dashboardPrintElement && dashboardPrintElement.contains(el)) {
        return;
      }
      
      // Remove the large paginated table from DashboardUsersTable component
      const tableContainer = el.closest('div');
      if (tableContainer) {
        tableContainer.remove();
      } else {
        el.remove();
      }
    });

    // Generate complete users table HTML with all data
    const usersTableHTML = `
      <div>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Business Unit</th>
              <th>Messages</th>
              <th>Cost (€)</th>
              <th>Input Tokens</th>
              <th>Output Tokens</th>
              <th>Most Used Model</th>
            </tr>
          </thead>
          <tbody>
            ${processedUsers.map(user => `
              <tr>
                <td>${user.display_name || ''}</td>
                <td>${user.status || ''}</td>
                <td>${user.business_unit || 'Unassigned'}</td>
                <td>${user.total_messages || 0}</td>
                <td>${user.total_cost?.toFixed(2) || '0.00'}</td>
                <td>${user.tokens_input || 0}</td>
                <td>${user.tokens_output || 0}</td>
                <td>${user.most_used_model || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bolt - Dashboard Report</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: A4 portrait;
              margin: 15mm 10mm;
            }
            
            body { 
              font-family: Arial, sans-serif;
              font-size: 11px;
              background: #fff;
              color: #000;
            }
            
            .header {
              margin-bottom: 12px;
              border-bottom: 2px solid #333;
              padding-bottom: 8px;
            }
            
            .header h1 {
              font-size: 20px;
              margin-bottom: 6px;
            }
            
            .header p {
              font-size: 10px;
              margin: 2px 0;
            }
            
            .dashboard-print {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
              width: 100%;
            }
            
            .dashboard-print > .MuiPaper-root {
              width: 100% !important;
              height: 250px !important;
              page-break-inside: avoid;
              background: #fff !important;
              border: 1px solid #ccc !important;
              border-radius: 4px !important;
              padding: 10px !important;
              page-break-after: auto;
              overflow: hidden !important;
              display: flex !important;
              flex-direction: column !important;
              max-height: 300px !important;
            }
            
            .dashboard-print .MuiCardContent-root {
              transform: scale(0.8);
              transform-origin: top left;
              width: 125%;
            }
            
            .dashboard-print h6,
            .dashboard-print .MuiTypography-h6 {
              font-size: 12px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #000;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }
            
            th, td {
              border: 1px solid #ccc;
              padding: 4px;
              text-align: left;
            }
            
            th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            
            /* Hide sort indicators in print */
            .MuiTableSortLabel-icon {
              display: none !important;
            }
            
            tr:nth-child(even) {
              background-color: #fafafa;
            }
            
            .dashboard-print {
              page-break-after: always;
            }
            
            /* Hide filter controls in print */
            .MuiFormControl-root,
            .MuiTextField-root {
              display: none !important;
            }
            
            /* Hide pagination in print */
            .MuiTablePagination-root {
              display: none !important;
            }
            
            svg {
              max-width: 100%;
              height: auto;
            }
            
            .recharts-surface {
              max-width: 100% !important;
            }
            
            .MuiCircularProgress-root {
              display: none;
            }
            
            @media print {
              body { 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .dashboard-print > * {
                break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Bolt - Dashboard Report</h1>
            <p><strong>Date Range:</strong> ${startDate?.toLocaleDateString()} - ${endDate?.toLocaleDateString()}</p>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          </div>
          ${clonedContent.innerHTML}
          ${usersTableHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Common card style
  const cardStyle = {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    height: 300,
    minHeight: 300
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mt: 3 }}>
        {showDashboardStaleWarning ? (
          <Box sx={{ mb: 2 }}>
            <QueryStaleDataWarning
              message="Showing last synced dashboard data. We couldn't refresh some metrics."
              onRetry={() => {
                void retryDashboardQueries();
              }}
            />
          </Box>
        ) : null}

        {showDashboardBlockingError ? (
          <QueryErrorState
            title="Unable to load dashboard"
            message={dashboardErrorMessage}
            onRetry={() => {
              void retryDashboardQueries();
            }}
            minHeight={260}
          />
        ) : (
        <>
        {/* Toolbar with Date Range and Print Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', md: 'center' }, 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Date Range Selector */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }
                  }
                }
              }}
            />
            <Typography sx={{ color: '#94a3b8' }}>to</Typography>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }
                  }
                }
              }}
            />
          </Box>

          {/* Export and Print Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{
                borderColor: '#3b82f6',
                color: '#3b82f6',
                '&:hover': {
                  borderColor: '#60a5fa',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)'
                }
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                borderColor: '#3b82f6',
                color: '#3b82f6',
                '&:hover': {
                  borderColor: '#60a5fa',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)'
                }
              }}
            >
              Print Report
            </Button>
          </Box>
        </Box>

        {/* Dashboard Cards and Table */}
        <Box 
          ref={printRef}
          sx={{display: 'block'}}
        >
          <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3}}>
            <Box 
              ref={dashboardRef}
              className="dashboard-print"
              sx={{ 
                display: 'grid', 
                gap: 3,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fit, minmax(280px, 1fr))',
                  lg: 'repeat(auto-fit, minmax(320px, 1fr))'
                }
              }}
            >

            {/* User Activity Table */}
            <Card sx={cardStyle}>
              <CardContent sx={{ height: '80%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 4, fontSize: '1rem' }}>
                  Total Users
                </Typography>
                {loadingUsersWithMetrics ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                    <CircularProgress />
                  </Box>
                ) : errorUsersWithMetrics ? (
                  <Alert severity="error">{errorUsersWithMetrics.message}</Alert>
                ) : !usersWithMetrics ? (
                  <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                    No data available for the selected date range
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                            ✅ Active Users
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {usersWithMetrics.filter(user => user.status.toLowerCase() === 'active').length.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                            ❌ Inactive Users
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {usersWithMetrics.filter(user => user.status.toLowerCase() === 'inactive').length.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            Total
                          </TableCell>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {usersWithMetrics.length.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Total Messages Card */}
            <Card sx={cardStyle}>
              <CardContent sx={{ height: '80%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 4, fontSize: '1rem' }}>
                  Total Messages
                </Typography>
                {loadingTotals ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : errorTotals ? (
                  <Alert severity="error">{errorTotals.message}</Alert>
                ) : !messageTotals ? (
                  <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                    No data available for the selected date range
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                            📥 Received Messages
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {messageTotals.sent_messages.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#fff', borderBottom: '1px solid #334155', fontSize: '1rem' }}>
                            📤 Sent Messages
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid #334155', textAlign: 'right', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {messageTotals.received_messages.toLocaleString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', fontSize: '1.1rem', fontWeight: 'bold' }}>
                            Total
                          </TableCell>
                          <TableCell sx={{ color: '#3b82f6', borderBottom: 'none', textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }}>
                            {messageTotals.total_messages.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Daily Messages and Average per Active User */}
            <DailyMessageChart 
              data={dailyMessageActivityResponse?.metrics || []}
              isLoading={loadingDailyMessageActivity}
              error={errorDailyMessageActivity}
            />


            {/* Most Active Users BarChart */}
            <Card sx={cardStyle}>
              <CardContent sx={{ height: '85%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 2, fontSize: '1rem' }}>
                  Most Active Users
                </Typography>
                {loadingUsers ? (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                    <CircularProgress />
                  </Box>) : errorUsers ? (<Alert severity="error">{errorUsers.message}</Alert>) : !mostActiveUsers ? (
                  <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                    No data available for the selected date range
                  </Typography>
                ) : (
                <ResponsiveContainer width="100%">
                  <BarChart layout='vertical' data={mostActiveUsers?.map(user => ({ name: user.display_name, messages: user.message_count }))} margin={{ top: 0, right: 25, left: 0, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <YAxis 
                      dataKey="name" 
                      type="category"
                      stroke="#94a3b8"
                      fontSize="0.9rem"
                      tick={{ fill: '#94a3b8' }}
                      width={180}
                    />
                    <XAxis 
                      type="number"
                      domain={[0, 'dataMax']}
                      stroke="#94a3b8"
                      fontSize="0.9rem"
                      tick={{ fill: '#94a3b8' }}
                      label={{ value: 'Messages', angle: 0, position: 'top', fill: '#94a3b8', offset: -35 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        color: '#fff'
                      }}
                      formatter={(value: any) => [`${value.toLocaleString()}`, 'Messages']}
                    />
                    <Bar dataKey="messages" fill="#3b82f6" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>)}
              </CardContent>
            </Card>

            {/* Model Usage BarChart */}
            <Card sx={cardStyle}>
              <CardContent sx={{ height: '80%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 5, fontSize: '1rem' }}>
                  Models Used Most
                </Typography>
                {loadingModels ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                    <CircularProgress />
                  </Box>) : errorModels ? (
                  <Alert severity="error">{errorModels.message}</Alert>
                ) : !modelUsage ? (
                  <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                    No data available for the selected date range
                  </Typography>
                ) : (
                <ResponsiveContainer width="100%">
                  <BarChart data={modelUsage?.map(model => ({ name: model.model_name, requests: model.request_count }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#94a3b8"
                      fontSize="0.9rem"
                      tick={{ fill: '#94a3b8' }}
                    />
                    <YAxis 
                      stroke="#94a3b8"
                      tick={{ fill: '#94a3b8' }}
                      label={{ value: 'Requests', angle: 0, position: 'top', fill: '#94a3b8', offset: 10 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value: any) => [value.toLocaleString(), 'Requests']}
                    />
                    <Bar dataKey="requests" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>)}
              </CardContent>
            </Card>

            {/* Costs per Business Unit Bar Chart */}
            <Card sx={cardStyle}>
              <CardContent sx={{ height: '80%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 5, fontSize: '1rem' }}>
                  Costs per Business Unit (€)
                </Typography>
                {loadingBusinessUnitCosts ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                    <CircularProgress />
                  </Box>) : errorBusinessUnitCosts ? (
                  <Alert severity="error">{errorBusinessUnitCosts.message}</Alert>
                ) : !businessUnitCosts ? (
                  <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                    No data available for the selected date range
                  </Typography>
                ) : (
                <ResponsiveContainer width="100%">
                  <BarChart data={businessUnitCosts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="unit" 
                      stroke="#94a3b8"
                      fontSize="0.9rem"
                      tick={{ fill: '#94a3b8' }}
                    />
                    <YAxis 
                      stroke="#94a3b8"
                      tick={{ fill: '#94a3b8' }}
                      label={{ value: '$', angle: 0, position: 'top', fill: '#94a3b8', offset: 10 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value: any) => [`${value.toLocaleString()} €`, 'Cost']}
                    />
                    <Bar dataKey="cost" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Team Costs Over Time Line Chart */}
            <Card sx={cardStyle}>
              <CardContent sx={{ height: '80%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 5, fontSize: '1rem' }}>
                  Costs of Your Team Members (€)
                </Typography>
                {loadingMonthlyTotalCosts ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: "100%" }}>
                    <CircularProgress />
                  </Box>) : errorMonthlyTotalCosts ? (
                  <Alert severity="error">{errorMonthlyTotalCosts.message}</Alert>
                ) : !monthlyTotalCosts ? (
                  <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                    No data available for the selected date range
                  </Typography>
                ) : (
                <ResponsiveContainer width="100%">
                  <LineChart data={monthlyTotalCosts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#94a3b8"
                      fontSize="0.9rem"
                      tick={{ fill: '#94a3b8' }}
                    />
                    <YAxis 
                      stroke="#94a3b8"
                      tick={{ fill: '#94a3b8' }}
                      hide
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value: any) => [`${value.toLocaleString()} €`, 'Cost']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cost" 
                      stroke="#60a5fa" 
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>)}
              </CardContent>
            </Card>

            {/* TTFT by Model Chart */}
            <TTFTByModelChart
              data={ttftByModelResponse?.metrics}
              isLoading={loadingTTFTByModel}
              error={errorTTFTByModel}
            />

            {/* Duration by Agent Chart */}
            <DurationByAgentChart
              data={durationByAgentResponse?.metrics}
              isLoading={loadingDurationByAgent}
              error={errorDurationByAgent}
            />            
            </Box>
          </Box>
          
          {/* User metrics table */}
          <Box sx={{ mt: 3}}>
               {errorUsersWithMetrics ? (
                  <Alert severity="error">{errorUsersWithMetrics.message}</Alert>
                ) : (
                  <DashboardUsersTable 
                    users={usersWithMetricsForTable} 
                    isLoading={loadingUsersWithMetrics}
                    onProcessedUsersChange={setProcessedUsers}
                  />
                )
              }
          </Box>
        </Box>
        </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardTab;
